import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Button,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
} from '@mui/material';
import {
  PlayArrow,
  Close,
  Apple,
  Android,
  SportsEsports,
  Computer,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

interface Game {
  id: number;
  title: string;
  image: string;
  description: string;
  longDescription: string;
  genre: string[];
  platform: string[];
  releaseDate: string;
  status: 'released' | 'in-development' | 'upcoming';
  stats?: {
    downloads?: string;
    rating?: number;
    players?: string;
  };
  features?: string[];
}

const games: Game[] = [
  {
    id: 1,
    title: 'Monster Truck Racing',
    image: '/Icons/Monster Truck Racing.png',
    description: 'Experience the thrill of monster truck racing with realistic physics, stunning graphics, and exciting gameplay.',
    longDescription: 'Monster Truck Racing brings the excitement of monster truck racing to your mobile device. With realistic physics simulation, beautiful 3D graphics, and challenging tracks, this game offers hours of entertainment. Navigate through various obstacles, perform amazing stunts, and dominate the racing arena.',
    genre: ['Racing', 'Action', 'Sports'],
    platform: ['Android', 'iOS'],
    releaseDate: 'In Development - 2025',
    status: 'in-development',
    features: [
      'Realistic physics engine for authentic truck behavior',
      'Multiple challenging tracks and environments',
      'Stunning 3D graphics and visual effects',
      'Easy-to-learn controls for all skill levels',
      'Exciting stunts and obstacle courses',
    ],
  },
  {
    id: 2,
    title: 'Truck Rival Chase',
    image: '/Icons/Truck Rival Chase.jpeg',
    description: 'The next evolution in monster truck gaming - featuring competitive multiplayer racing and advanced vehicle customization.',
    longDescription: 'Truck Rival Chase is our upcoming title that takes monster truck racing to the next level. This game will feature competitive multiplayer modes, extensive vehicle customization options, and even more spectacular tracks and environments. Get ready for the ultimate monster truck experience.',
    genre: ['Racing', 'Action', 'Multiplayer'],
    platform: ['Android', 'iOS'],
    releaseDate: 'In Development - 2025',
    status: 'in-development',
    features: [
      'Competitive multiplayer racing modes',
      'Extensive vehicle customization system',
      'New and improved physics engine',
      'Cross-platform multiplayer support',
      'Career mode with progression system',
      'Special events and seasonal challenges',
    ],
  },
];

const Games = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);

  const filteredGames = games.filter(game => {
    if (selectedTab === 0) return true;
    if (selectedTab === 1) return game.status === 'released';
    if (selectedTab === 2) return game.status === 'in-development';
    if (selectedTab === 3) return game.status === 'upcoming';
    return true;
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const getPlatformIcon = (platform: string) => {
    switch(platform.toLowerCase()) {
      case 'pc': return <Computer sx={{ fontSize: 16 }} />;
      case 'ps5': case 'xbox series x': return <SportsEsports sx={{ fontSize: 16 }} />;
      case 'mobile': case 'android': return <Android sx={{ fontSize: 16 }} />;
      case 'ios': return <Apple sx={{ fontSize: 16 }} />;
      default: return null;
    }
  };
  return (
    <Box sx={{ py: 8, minHeight: '100vh' }}>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom 
            align="center"
            sx={{ fontWeight: 'bold', mb: 2 }}
          >
            Our Games Portfolio
          </Typography>
          <Typography variant="h5" align="center" color="text.secondary" sx={{ mb: 6 }}>
            Mobile games built with Unity, designed for fun and excitement on Android & iOS
          </Typography>

          {/* Filter Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 6 }}>
            <Tabs 
              value={selectedTab} 
              onChange={handleTabChange} 
              centered
              sx={{
                '& .MuiTab-root': {
                  fontSize: '1rem',
                  fontWeight: 600,
                  px: 4,
                },
              }}
            >
              <Tab label="All Games" />
              <Tab label="Released" />
              <Tab label="In Development" />
              <Tab label="Upcoming" />
            </Tabs>
          </Box>

          {/* Games Grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 4 }}>
            {filteredGames.map((game, index) => (
              <motion.div
                key={game.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'background.paper',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'all 0.3s',
                    border: '1px solid transparent',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      borderColor: 'primary.main',
                      boxShadow: '0 12px 24px rgba(229,231,235,0.25)',
                    },
                  }}
                >
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={game.image}
                        alt={game.title}
                        sx={{ objectFit: 'cover' }}
                      />
                      <Chip
                        label={game.status.replace('-', ' ').toUpperCase()}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          bgcolor: game.status === 'released' ? '#4CAF50' : 
                                   game.status === 'in-development' ? '#FF9800' : '#2196F3',
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      />
                    </Box>
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {game.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {game.description}
                      </Typography>

                      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                        {game.genre.slice(0, 3).map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(229,231,235,0.15)',
                              color: 'primary.main',
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </Stack>

                      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                        {game.platform.slice(0, 3).map((platform) => (
                          <Chip
                            key={platform}
                            icon={getPlatformIcon(platform) || undefined}
                            label={platform}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                          />
                        ))}
                      </Stack>

                      {game.stats && (
                        <Stack spacing={1} sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Downloads: <strong>{game.stats.downloads}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Rating: <strong>{game.stats.rating}/5.0</strong> ⭐
                          </Typography>
                        </Stack>
                      )}

                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        {game.releaseDate}
                      </Typography>

                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<PlayArrow />}
                        onClick={() => setSelectedGame(game)}
                        sx={{
                          background: 'linear-gradient(135deg, #374151 0%, #1F2937 100%)',
                          color: '#FFFFFF',
                          fontWeight: 600,
                          borderRadius: '12px',
                          py: 1.5,
                          boxShadow: '0 4px 14px rgba(55, 65, 81, 0.25)',
                          transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
                            transform: 'translateY(-3px)',
                            boxShadow: '0 8px 25px rgba(55, 65, 81, 0.35)',
                          },
                          '&:active': {
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        Learn More
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
            ))}
          </Box>
        </motion.div>
      </Container>

      {/* Game Detail Dialog */}
      <Dialog
        open={Boolean(selectedGame)}
        onClose={() => setSelectedGame(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedGame && (
          <DialogContent sx={{ p: 0, bgcolor: 'background.paper' }}>
            <IconButton
              onClick={() => setSelectedGame(null)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.5)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                zIndex: 1,
              }}
            >
              <Close />
            </IconButton>
            <CardMedia
              component="img"
              height="300"
              image={selectedGame.image}
              alt={selectedGame.title}
            />
            <Box sx={{ p: 4 }}>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                {selectedGame.title}
              </Typography>
              
              <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                {selectedGame.genre.map((tag) => (
                  <Chip key={tag} label={tag} color="primary" />
                ))}
              </Stack>

              <Typography variant="body1" paragraph>
                {selectedGame.longDescription}
              </Typography>

              {selectedGame.features && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Key Features:
                  </Typography>
                  <Stack spacing={1}>
                    {selectedGame.features.map((feature, idx) => (
                      <Typography key={idx} variant="body2">
                        • {feature}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}

              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Available on:
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                {selectedGame.platform.map((platform) => (
                  <Chip
                    key={platform}
                    icon={getPlatformIcon(platform) || undefined}
                    label={platform}
                    variant="outlined"
                  />
                ))}
              </Stack>

              {selectedGame.stats && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.default' }}>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                      {selectedGame.stats.downloads}
                    </Typography>
                    <Typography variant="caption">Downloads</Typography>
                  </Paper>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.default' }}>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                      {selectedGame.stats.rating}
                    </Typography>
                    <Typography variant="caption">Rating</Typography>
                  </Paper>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.default' }}>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                      {selectedGame.stats.players}
                    </Typography>
                    <Typography variant="caption">Active Players</Typography>
                  </Paper>
                </Box>
              )}

              <Typography variant="body2" color="text.secondary">
                {selectedGame.releaseDate}
              </Typography>
            </Box>
          </DialogContent>
        )}
      </Dialog>
    </Box>
  );
};

export default Games; 
